import {
  collection,
  onSnapshot,
  query,
  where,
  type CollectionReference,
  type DocumentData,
  type Unsubscribe,
} from "firebase/firestore";

import { db } from "@/apis/config/firebase";
import type {
  ILicenseRequest,
  LicenseRequestStatus,
  SoftwareType,
} from "@/features/licenses/schemas/license";

export const LICENSE_REQUESTS_COLLECTION = "license_requests";

function getCollection(): CollectionReference<DocumentData> {
  return collection(db, LICENSE_REQUESTS_COLLECTION);
}

function mapDoc(id: string, data: DocumentData): ILicenseRequest {
  const status = data.status;
  const normalizedStatus: LicenseRequestStatus =
    status === "approved" || status === "rejected" || status === "pending"
      ? status
      : "pending";

  return {
    id,
    userId: typeof data.userId === "string" ? data.userId : "",
    machineId: typeof data.machineId === "string" ? data.machineId : "",
    clientName: typeof data.clientName === "string" ? data.clientName : "",
    softwareType: (typeof data.softwareType === "string"
      ? data.softwareType
      : "") as SoftwareType,
    status: normalizedStatus,
    createdAt:
      typeof data.createdAt === "string"
        ? data.createdAt
        : data.createdAt?.toDate?.()?.toISOString?.() ?? "",
    updatedAt:
      typeof data.updatedAt === "string"
        ? data.updatedAt
        : data.updatedAt?.toDate?.()?.toISOString?.() ?? "",
    licenseId: typeof data.licenseId === "string" ? data.licenseId : undefined,
    decidedBy: typeof data.decidedBy === "string" ? data.decidedBy : undefined,
    decidedAt:
      typeof data.decidedAt === "string"
        ? data.decidedAt
        : data.decidedAt?.toDate?.()?.toISOString?.() ?? undefined,
    reason: typeof data.reason === "string" ? data.reason : undefined,
  };
}

function sortByCreatedDesc(a: ILicenseRequest, b: ILicenseRequest): number {
  return (b.createdAt || "").localeCompare(a.createdAt || "");
}

export type LicenseRequestsQuery = {
  status?: LicenseRequestStatus;
  userId?: string;
};

/**
 * Suscripción realtime a `license_requests` (filtros opcionales).
 */
export function subscribeLicenseRequests(
  options: LicenseRequestsQuery,
  onData: (rows: ILicenseRequest[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const constraints = [];
  if (options.userId) {
    constraints.push(where("userId", "==", options.userId));
  }
  if (options.status) {
    constraints.push(where("status", "==", options.status));
  }

  const q =
    constraints.length > 0
      ? query(getCollection(), ...constraints)
      : query(getCollection());

  return onSnapshot(
    q,
    (snapshot) => {
      const rows = snapshot.docs
        .map((d) => mapDoc(d.id, d.data()))
        .sort(sortByCreatedDesc);
      onData(rows);
    },
    (error) => {
      console.error("Error escuchando license_requests:", error);
      onError?.(error instanceof Error ? error : new Error(String(error)));
    }
  );
}
